<template>
  <c-box class="return_details main-content">
    <main
      id="content"
      v-chakra
      :mt="{ base: 'xl', sm: '4xl', md: 0 }"
      :mx="{ base: '-20px', sm: 0 }"
    >
      <submitter-details-mobile
        v-if="returnData && returnData"
        :contact-details="returnData.contactDetails"
      />

      <c-box :my="{ base: 'md', md: '3.5rem' }">
        <c-heading
          as="h1"
          :font-size="{ base: '2xl', md: '4xl' }"
          mt="md"
          mb="xs"
          color="brand.purpleDark"
          font-weight="normal"
        >
          {{ $t("productReturnV2.details.maintenanceReturn.title") }}
        </c-heading>
        <c-heading as="h2" font-size="md" color="deepBlack" font-weight="normal">
          {{ displayId }}
        </c-heading>
      </c-box>

      <c-divider width="100%" border-color="gray.300" :mb="{ base: 'md', md: '3xl' }" />

      <c-flex v-if="isLoading" width="100%" justify-content="center">
        <Spinner />
      </c-flex>
      <c-box v-else-if="returnData">
        <c-grid
          :template-columns="{
            md: 'repeat(3, 1fr)',
            lg:
              returnData.shipmentDetails.shipperType === 'DHL'
                ? 'repeat(3, 1fr)'
                : 'repeat(4, 1fr)',
          }"
          gap="6"
        >
          <c-grid-item>
            <c-heading as="h3" size="sm" font-weight="normal" mb="sm">
              {{ $t("productReturn.displaySingle.submitted") }}
            </c-heading>
            <p>
              {{
                $t("productReturn.displaySingle.submittedBy", {
                  date: moment(returnData.created).format("D.M.YYYY"),
                  creator: returnData.contactDetails.fullName,
                })
              }}
            </p>
          </c-grid-item>
          <!-- c-grid-item's col-span doesn't support object syntax, hence array syntax :( -->
          <c-grid-item :col-span="[1, 1, 1, 2]">
            <c-heading as="h3" size="sm" font-weight="normal" mb="sm">
              {{ $t("productReturn.displaySingle.overallStatus") }}
            </c-heading>

            <c-flex
              v-if="returnData.status"
              :align-items="{ base: 'center', md: 'flex-start' }"
              :flex-direction="{ md: 'column' }"
            >
              <c-box
                mr="sm"
                v-chakra="{
                  '.overall-return-progress > div': {
                    backgroundColor: 'brand.purple',
                  },
                  '.FINISHED > div': {
                    backgroundColor: 'brand.green',
                  },
                  '.CANCELLED > div': {
                    backgroundColor: 'brand.redLight',
                  },
                  '.UNKNOWN > div': {
                    backgroundColor: 'brand.purpleLight',
                  },
                }"
              >
                <c-progress
                  class="overall-return-progress"
                  :class="returnData.status"
                  :value="returnData.progress"
                  border-radius="full"
                  size="lg"
                  background="gray.200"
                  width="100%"
                  :min-width="{ base: '5rem', md: '10rem' }"
                  :mb="{ md: 'xs' }"
                />
              </c-box>
              {{ $t(`productReturnV2.returnStatus.${returnData.status}`) }}
              <c-box
                v-if="showGeneralWarning(returnData)"
                :title="$t('productReturn.warning.seeBelow')"
                ml="2xs"
              >
                <c-icon name="alert" size="1.4rem" color="brand.orange" />
              </c-box>
            </c-flex>
            <c-flex v-else align-items="center">
              <c-box
                mr="sm"
                v-chakra="{
                  '.overall-return-progress > div': {
                    backgroundColor: 'brand.purpleLight',
                  },
                }"
              >
                <c-progress
                  has-stripe
                  :value="100"
                  border-radius="full"
                  size="lg"
                  background="gray.200"
                  width="100%"
                  min-width="5rem"
                />
              </c-box>
              {{ $t(`productReturn.combinedStatus.UNKNOWN`) }}
            </c-flex>
          </c-grid-item>
          <c-grid-item v-if="returnData.shipmentDetails.shipperType === 'DHL'" col-span="3">
            <RMADHLShippingInformation
              :tracking-codes="returnData.shipmentDetails.trackingCodes"
              :planned-shipping-date="returnData.shipmentDetails.plannedShippingDate"
              :country-code="returnData.contactDetails.partnerCountry"
              :zip-code="returnData.contactDetails.partnerZipCode"
              :return-type="returnData.returnType"
              :rma-id="returnData.rmaId"
              :parcel-label-and-pro-forma-id="returnData.id"
              :pro-forma-available="returnData.shipmentDetails.proFormaAvailable"
            />
          </c-grid-item>
          <c-grid-item
            v-else-if="returnData.shipmentDetails.shipperType === 'CONTACT_CS'"
            :ml="{ md: 'auto' }"
          >
            <c-icon name="info" size="1.8rem" color="brand.black" m="-6px 0 0 -4px" />{{
              $t("productReturnV2.details.contactCsInfo")
            }}
          </c-grid-item>
          <c-grid-item
            v-else-if="returnData.shipmentDetails.shipperType !== 'MANUAL'"
            :ml="{ md: 'auto' }"
          >
            <c-heading as="h3" size="sm" font-weight="normal" mb="sm">
              {{ $t("productReturn.displaySingle.printParcelLabel") }}
            </c-heading>
            <c-box>
              <RMADownloadParcel
                :return-id="returnData.id"
                :return-type="returnData.returnType"
                v-chakra
                :width="{ md: '100%' }"
                max-width="20rem"
                mb="xs"
                display="block"
              />
              <RMADownloadInternationalForm
                v-if="returnData.shipmentDetails.proFormaAvailable"
                :return-id="returnData.id"
                :return-type="returnData.returnType"
                v-chakra
                :width="{ md: '100%' }"
                max-width="20rem"
                mb="xs"
                display="block"
              />
              <RMAParcelTrackingLink
                v-if="trackingCodesExist()"
                :rma-id="returnData.rmaId"
                link-type="link"
                v-chakra
                :width="{ md: '100%' }"
                max-width="20rem"
                mb="xs"
                display="block"
              />
            </c-box>
          </c-grid-item>
        </c-grid>

        <c-divider width="100%" border-color="gray.300" :my="{ base: 'md', md: '3xl' }" />

        <c-box
          v-for="(site, siteIndex) in returnData.productDetails.sites"
          :key="`maintenance_return__site_${siteIndex}`"
          width="100%"
        >
          <c-flex :m="{ base: 0 }" justify-content="space-between" width="100%" my="3xl">
            <c-box>
              <c-heading as="h2" size="lg" font-weight="normal" mb="xs">
                {{ site.siteDetails.name || $t("productReturnV2.site.nameUnknown") }}
              </c-heading>
              <c-heading as="h3" size="sm" font-weight="normal">
                {{ $t("productReturnV2.details.reference") }}:
                {{ site.siteDetails.reference || "-" }}
              </c-heading>
            </c-box>

            <c-flex
              v-if="siteIndex === 0"
              :align-items="{ base: 'flex-end', md: 'center' }"
              text-align="right"
            >
              <c-button
                :title="$t('common.expandAll')"
                @click="expandAll"
                mt="md"
                :ml="{ md: 'md' }"
                py="0.5rem"
                :pl="{ base: '0.25rem', md: 'lg' }"
                :pr="{ base: '0', md: 'xl' }"
                left-icon="expand"
                color="brand.purple"
              >
                <c-text as="span" :display="{ base: 'none', md: 'inline' }">
                  {{ $t("common.expandAll") }}
                </c-text>
              </c-button>
              <c-button
                :title="$t('common.collapseAll')"
                @click="collapseAll"
                mt="md"
                :ml="{ base: 'xs', md: 'md' }"
                py="0.5rem"
                :pl="{ base: '0.25rem', md: 'lg' }"
                :pr="{ base: '0', md: 'xl' }"
                left-icon="minimize"
                color="brand.purple"
              >
                <c-text as="span" :display="{ base: 'none', md: 'inline' }">
                  {{ $t("common.collapseAll") }}
                </c-text>
              </c-button>
            </c-flex>
          </c-flex>

          <c-box :mb="{ sm: 'md', md: '3xl' }">
            <table
              v-chakra
              :display="{ md: 'flex' }"
              :flex-direction="{ md: 'column' }"
              width="100%"
              text-align="left"
            >
              <thead
                :display="{ base: 'none', md: 'table-header-group' }"
                v-chakra="{
                  '& th': {
                    fontWeight: 'normal',
                    pb: '0.75rem',
                  },
                }"
              >
                <tr v-chakra display="grid" p="0" grid-template-columns="7rem 2fr 2fr 2fr 2fr 7rem">
                  <th v-chakra pl="0">{{ $t("common.qty") }}</th>
                  <th>{{ $t("productReturnV2.details.productSku") }}</th>
                  <th>{{ $t("productReturnV2.details.productName") }}</th>
                  <th>{{ $t("productReturnV2.details.reasonToReturn") }}</th>
                  <th>
                    {{ $t("productReturnV2.details.additionalDetails") }}
                  </th>
                  <th>{{ $t("productReturnV2.details.status") }}</th>
                </tr>
              </thead>

              <tbody
                v-for="(product, index) in site.products"
                :key="`${product.sku}_${index}`"
                :display="{ base: 'flex', md: 'table-row-group' }"
                flex-direction="column"
                width="100%"
                v-chakra="{
                  '& td': {
                    py: { base: '0.25rem', md: 'md' },
                    pr: 'md',
                    pl: 0,
                    display: { base: 'flex', md: 'table-cell' },
                    flexDirection: 'row',
                    alignContent: 'center',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  },
                  '& tr > td': {
                    fontSize: { base: '0.85rem', md: 'inherit' },
                  },
                }"
              >
                <tr
                  @click="product.expanded = !product.expanded"
                  v-chakra
                  cursor="pointer"
                  :display="{ md: 'grid' }"
                  grid-template-columns="7rem 2fr 2fr 2fr 2fr 7rem"
                  border-top="1px solid"
                  border-color="gray.300"
                  :pt="{ base: 'md', md: '0' }"
                >
                  <td>
                    <c-text as="span" :display="{ md: 'none' }" mr="xs" font-weight="bold">
                      {{ $t("common.qty") }}
                    </c-text>
                    <button id="exp1">
                      <c-icon
                        :name="product.expanded ? 'chevron_down' : 'chevron_right'"
                        size="0.8rem"
                        color="brand.purple"
                        mr="xs"
                      />
                      {{ product.instances.length }}
                      {{ $t("common.pcs") }}
                    </button>
                  </td>
                  <td>
                    <c-text as="span" :display="{ md: 'none' }" mr="xs" font-weight="bold">
                      {{ $t("productReturnV2.details.productSku") }}
                    </c-text>
                    <c-text>{{ product.sku }}</c-text>
                  </td>
                  <td>
                    <c-text as="span" :display="{ md: 'none' }" mr="xs" font-weight="bold">
                      {{ $t("productReturnV2.details.productName") }}
                    </c-text>
                    <c-text>{{ productNameCache[product.sku] || "-" }}</c-text>
                  </td>
                  <td>
                    <c-text as="span" :display="{ md: 'none' }" mr="xs" font-weight="bold">
                      {{ $t("productReturnV2.details.reasonToReturn") }}
                    </c-text>
                    <c-text>
                      {{ generateReason(product) }}
                    </c-text>
                  </td>
                  <td>
                    <c-text as="span" :display="{ md: 'none' }" mr="xs" font-weight="bold">
                      {{ $t("productReturnV2.details.additionalDetails") }}
                    </c-text>
                    <c-text>{{ product.additionalInformation || "-" }}</c-text>
                  </td>
                  <td align-items="center">
                    <c-text as="span" :display="{ md: 'none' }" mr="xs" font-weight="bold">
                      {{ $t("productReturnV2.details.status") }}
                    </c-text>
                    <c-box>
                      <c-box
                        :mr="{ md: 'sm' }"
                        v-chakra="{
                          '.site-return-progress > div': {
                            backgroundColor: 'brand.purple',
                          },
                          '.FINISHED > div': {
                            backgroundColor: 'brand.green',
                          },
                          '.CANCELLED > div': {
                            backgroundColor: 'brand.redLight',
                          },
                          '.UNKNOWN > div': {
                            backgroundColor: 'brand.purpleLight',
                          },
                        }"
                      >
                        <c-progress
                          class="site-return-progress"
                          :class="returnData.status"
                          :value="returnData.progress"
                          border-radius="full"
                          size="lg"
                          background="gray.200"
                          width="100%"
                          min-width="5rem"
                        />
                      </c-box>
                      <c-box v-if="quotationSentForProduct(product)">
                        <c-text>
                          <c-icon name="alert" size="1.5rem" color="brand.orange" />
                          {{ $t("productReturnV2.warning.detail.REPAIR_OFFER_SENT") }}
                        </c-text>
                      </c-box>
                    </c-box>
                    <c-text class="sr-only">{{ product.progress }} / 100</c-text>
                  </td>
                </tr>
                <tr v-if="product.expanded" class="sr-only">
                  <th scope="row" colspan="6">
                    {{ $t("srOnly.listProductItems") }}
                  </th>
                </tr>
                <tr
                  v-if="product.expanded"
                  display="block"
                  v-chakra="{
                    '& > td': {
                      display: { base: 'flex', md: 'block' },
                    },
                  }"
                >
                  <td colspan="6" v-chakra display="block">
                    <table
                      v-chakra
                      :width="{ base: '100%', md: 'calc(100% - 2rem)', lg: 'calc(100% - 7rem)' }"
                      :ml="{ md: '2rem', lg: '7rem' }"
                      :mt="{ base: 'md', md: '0' }"
                      font-size="0.85rem"
                    >
                      <thead
                        :display="{ base: 'none', md: 'table-header-group' }"
                        color="deepBlack"
                        v-chakra="{
                          '& th': {
                            fontWeight: 'normal',
                            pb: '0.75rem',
                          },
                        }"
                      >
                        <tr
                          v-chakra
                          :display="{ base: 'block', md: 'table-row' }"
                          :p="{ base: '0.5rem 0', md: '0' }"
                        >
                          <th>
                            {{ $t("productReturnV2.details.row.crNumber") }}
                          </th>
                          <th>
                            {{ $t("productReturnV2.details.row.serialNumber") }}
                          </th>
                          <th>
                            {{ $t("common.status") }}
                          </th>
                          <th>
                            {{ $t("productReturnV2.details.row.resolution") }}
                          </th>
                        </tr>
                      </thead>
                      <tbody
                        color="gray.700"
                        :display="{ base: 'grid', md: 'table-row-group' }"
                        v-chakra="{
                          '& td': {
                            verticalAlign: 'top',
                            pt: 0,
                            pr: { base: 0, md: 'md' },
                            pb: { base: 0, md: 'md' },
                            pl: 0,
                          },
                        }"
                      >
                        <tr
                          v-for="(instance, index) in product.instances"
                          :key="`${instance.serviceItemNumber}_${index}`"
                          v-chakra
                          :mb="{ base: 'md', md: '0' }"
                        >
                          <td>
                            <c-text as="span" :display="{ md: 'none' }" mr="xs" font-weight="bold">
                              {{ $t("productReturnV2.details.row.crNumber") }}
                            </c-text>
                            <c-text>{{ instance.serviceItemNumber }}</c-text>
                          </td>
                          <td>
                            <c-text as="span" :display="{ md: 'none' }" mr="xs" font-weight="bold">
                              {{ $t("productReturnV2.details.row.serialNumber") }}
                            </c-text>
                            <RMACRSerialNumber
                              :item-serial="instance.serial"
                              :return-date="returnData.created"
                              :input-id="`${instance.serviceItemNumber}_${index}`"
                              :input-number="index"
                              :disable-input="true"
                            ></RMACRSerialNumber>
                          </td>
                          <td v-chakra :max-width="{ md: '20rem' }">
                            <c-text as="span" :display="{ md: 'none' }" mr="xs" font-weight="bold">
                              {{ $t("common.status") }}
                            </c-text>

                            <c-flex
                              align-items="baseline"
                              :flex-direction="{ base: 'column', md: 'row' }"
                              ml="auto"
                            >
                              <c-box
                                :mr="{ md: 'sm' }"
                                v-chakra="{
                                  '.row-return-progress > div': {
                                    backgroundColor: 'brand.purple',
                                  },
                                  '.FINISHED > div': {
                                    backgroundColor: 'brand.green',
                                  },
                                  '.CANCELLED > div': {
                                    backgroundColor: 'brand.redLight',
                                  },
                                }"
                              >
                                <c-progress
                                  class="row-return-progress"
                                  :class="{
                                    FINISHED: instance.progress === 100,
                                    CANCELLED: areRowsCancelled({ instances: [instance] }),
                                  }"
                                  :value="returnData.progress"
                                  border-radius="full"
                                  size="md"
                                  background="gray.200"
                                  min-width="4rem"
                                />
                              </c-box>

                              <c-text>
                                {{
                                  $t(
                                    `productReturnV2.details.row.status.${instance.repairStatusForCustomer}`,
                                  )
                                }}
                              </c-text>
                            </c-flex>
                          </td>
                          <td v-chakra max-width="13rem">
                            <c-text as="span" :display="{ md: 'none' }" mr="xs" font-weight="bold">
                              {{ $t("productReturnV2.details.row.resolution") }}
                            </c-text>
                            <c-text>{{ instance.faultCodeDescription }}</c-text>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </c-box>
        </c-box>

        <submitter-details :contact-details="returnData.contactDetails" />
      </c-box>
    </main>
  </c-box>
</template>

<script lang="ts">
import SubmitterDetails from "@/components/returns/SubmitterDetails.vue";
import SubmitterDetailsMobile from "@/components/returns/SubmitterDetailsMobile.vue";
import Spinner from "@/components/Spinner.vue";
import Icon from "@/design-system/elements/Icon.vue";
import RMADHLShippingInformation from "@/views/rma/RMADHLShippingInformation.vue";
import RMADownloadInternationalForm from "@/views/rma/RMADownloadInternationalForm.vue";
import RMADownloadParcel from "@/views/rma/RMADownloadParcel.vue";
import RMAParcelTrackingLink from "@/views/rma/RMAParcelTrackingLink.vue";
import RMAProcessSteps from "@/views/rma/RMAProcessSteps.vue";
import {
  CBox,
  CButton,
  CDivider,
  CFlex,
  CGrid,
  CGridItem,
  CHeading,
  CIcon,
  CProgress,
  CText,
} from "@chakra-ui/vue";
import {
  getMaintenanceReturnUniqueSkus,
  IMaintenanceReturnProductResponse,
  IMaintenanceReturnResult,
  Logger,
  quotationSentForSomeLine,
  quotationSentForSomeProductLine,
  RepairStatusForCustomer,
  IRootState,
} from "@iloq/shared";
import { defineComponent } from "vue";
import { mapState } from "vuex";
import RMACRSerialNumber from "./rma/RMACRSerialNumber.vue";

interface IMaintenanceReturnData extends IMaintenanceReturnResult {
  expanded?: boolean;
}

interface IData {
  returnData?: IMaintenanceReturnData;
  displayId?: string;
  moment: any;
}

export default defineComponent({
  name: "MaintenanceReturnDetails",
  components: {
    RMADownloadParcel,
    RMAParcelTrackingLink,
    RMADownloadInternationalForm,
    RMAProcessSteps,
    Icon,
    CIcon,
    CHeading,
    CText,
    CBox,
    CFlex,
    CDivider,
    CGrid,
    CGridItem,
    CProgress,
    CButton,
    Spinner,
    SubmitterDetails,
    SubmitterDetailsMobile,
    RMADHLShippingInformation,
    RMACRSerialNumber,
  },
  props: {
    rmaId: {
      type: String,
      required: true,
    },
  },
  data(): IData {
    return {
      returnData: undefined,
      displayId: "",
      moment: (this as any).$moment,
    };
  },
  computed: {
    ...mapState({
      user: (state: unknown) => (state as IRootState).user!,
      returnListing: (state: unknown) => (state as IRootState).returns?.list,
      isLoading: (state: unknown) => (state as IRootState).returns?.loading,
      productNameCache: (state: unknown) => (state as IRootState).returns?.productNameCache || {},
    }),
  },
  mounted(): void {
    this.syncReturnData(this.rmaId);
  },
  methods: {
    syncReturnData(rmaId: string): void {
      this.$store.watch(
        (state: any) =>
          state.returns.list.find((item: IMaintenanceReturnResult) => item.rmaId === rmaId),
        (newValue: IMaintenanceReturnResult) => {
          this.updateReturnData(newValue);
        },
      );

      const returnData = this.returnListing?.find(
        (item: IMaintenanceReturnResult) => item.rmaId === rmaId,
      );
      if (returnData) {
        this.updateReturnData(returnData);
      }
    },
    updateReturnData(
      { productDetails, rmaId, ...rest }: IMaintenanceReturnData,
      expanded?: boolean,
    ): void {
      if (!this.displayId) {
        this.displayId = rmaId;
      }
      this.returnData = {
        rmaId,
        ...rest,
        productDetails: {
          ...productDetails,
          sites: productDetails?.sites?.map((site: any) => ({
            ...site,
            products: site.products.map((product: any) => ({ ...product, expanded: !!expanded })),
          })),
        },
      };
      const uniqueSkus = getMaintenanceReturnUniqueSkus(this.returnData);
      this.fetchProductNames(uniqueSkus);
    },
    async fetchProductNames(skus: string[]) {
      try {
        await this.$store.dispatch("returns/fetchProductNames", skus);
      } catch (error) {
        Logger.warn("MaintenanceReturns.fetchProductNames failed", error);
      }
    },
    expandAll() {
      this.updateReturnData(this.returnData!, true);
    },
    collapseAll() {
      this.updateReturnData(this.returnData!, false);
    },
    showGeneralWarning(data: IMaintenanceReturnResult): boolean {
      return quotationSentForSomeLine(data);
    },
    quotationSentForProduct(data: IMaintenanceReturnProductResponse): boolean {
      return quotationSentForSomeProductLine(data);
    },
    areRowsCancelled({ instances }: IMaintenanceReturnProductResponse): boolean {
      return !instances.some(
        (instance) => instance.repairStatusForCustomer !== RepairStatusForCustomer.CANCELLED,
      );
    },
    generateReason({ returnReason, problemOccurence }: IMaintenanceReturnProductResponse) {
      const reason = [
        this.$t(`productReturnV2.returnReason.${returnReason}`).toString(),
        this.$t(`productReturnV2.problemOccurence.${problemOccurence}`).toString().toLowerCase(),
      ];
      return reason.join(", ");
    },
    trackingCodesExist(): boolean {
      return (this.returnData?.shipmentDetails.trackingCodes || []).length > 0;
    },
  },
});
</script>
